import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import TEAM_PROP_TYPES from '../../lib/prop-types/team';
import LOADING_ERROR_PROP_TYPES from '../../lib/prop-types/loading-error';
import { SEASONS_PROP_TYPES } from '../../lib/prop-types/season';
import { COMPETITIONS_PROP_TYPES } from '../../lib/prop-types/competition';
import ClubRouter from '../../lib/routing';
import withTeam from '../../lib/with-team';
import NoBackground from '../../components/club-frame/header/no-background';
import SeoHead from '../../components/seo';
import TeamFixtures from '../../components/team/fixtures';
import {
  isLoading,
  hasLoaded,
  getLoadingError,
  getCompetitions,
  getFixtures,
} from '../../store/teams/fixtures/selectors';
import { fetchFixturesPageData } from '../../store/teams/fixtures/actions';
import { getCurrentClubSeasons, getActiveSeasonId } from '../../store/clubs/selectors';
import PageDataLoadError from '../../components/errors/page-data-load-error';

class TeamFixturesPage extends React.Component {
  static async getInitialProps({ reduxStore, club, isServer, team, req, res, query }) {
    const initialProps = {
      seasonId: query.season
        ? parseInt(query.season, 10)
        : getActiveSeasonId(reduxStore.getState()),
      competitionId: query.competition,
      activeSubSection: 'fixtures',
      pageType: 'team.fixtures',
    };

    if (!isServer) {
      return initialProps;
    }

    const { error, accessLevel } = await reduxStore.dispatch(
      fetchFixturesPageData(club.id, team.id, initialProps.seasonId, req),
    );

    if (error && error.errorStatusCode && res) {
      res.statusCode = error.errorStatusCode;
    }

    if (accessLevel && accessLevel === 'private') {
      res.doNotCache = true;
    }

    return initialProps;
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { teamId: prevTeamId, seasonId: prevSeasonId } = prevProps;
    const { teamId, seasonId } = this.props;

    if (teamId !== prevTeamId || (teamId === prevTeamId && seasonId !== prevSeasonId)) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const {
      loading,
      previouslyLoaded,
      loadPageData,
      club: { id: clubId },
      team: { id: teamId },
      seasonId,
    } = this.props;
    if (!loading && !previouslyLoaded) {
      loadPageData(clubId, teamId, seasonId);
    }
  };

  onSeasonFilterChangeHandler = (event) => {
    const seasonId = event.target.value;
    const {
      club,
      team: { id: teamId },
    } = this.props;

    ClubRouter.pushClubRoute(club, 'team.fixtures', { teamId, season: seasonId });
  };

  onCompetitionFilterChangeHandler = (event) => {
    const competitionId = event.target.value;
    const {
      club,
      team: { id: teamId },
      seasonId,
    } = this.props;

    const params = {
      teamId,
      competition: competitionId,
    };

    if (seasonId) {
      params.season = seasonId;
    }
    ClubRouter.pushClubRoute(club, 'team.fixtures', params);
  };

  render() {
    const {
      club,
      team,
      loadingError,
      loading,
      seasons,
      seasonId,
      competitions,
      competitionId,
      fixtures,
    } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    return (
      <>
        <SeoHead
          club={club}
          descriptionTranslationKey="teams:fixtures_meta_description"
          route="team.fixtures"
          routeParams={{ teamId: team.id }}
          titleTranslationKey="teams:fixtures_page_title"
          translationValues={{ teamName: team.name }}
        />
        <NoBackground />
        <TeamFixtures
          club={club}
          teamId={team.id}
          isLoading={loading}
          seasons={seasons}
          seasonId={seasonId}
          competitions={competitions}
          competitionId={competitionId}
          fixtures={fixtures}
          onSeasonFilterChange={this.onSeasonFilterChangeHandler}
          onCompetitionFilterChange={this.onCompetitionFilterChangeHandler}
        />
      </>
    );
  }
}

TeamFixturesPage.defaultProps = {
  loadingError: null,
  teamId: 0,
  seasonId: null,
  competitions: null,
  competitionId: null,
};

TeamFixturesPage.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  team: TEAM_PROP_TYPES.isRequired,
  teamId: PropTypes.number,
  seasonId: PropTypes.number,
  loadPageData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  previouslyLoaded: PropTypes.bool.isRequired,
  loadingError: LOADING_ERROR_PROP_TYPES,
  seasons: SEASONS_PROP_TYPES.isRequired,
  competitions: COMPETITIONS_PROP_TYPES,
  fixtures: PropTypes.shape().isRequired,
  competitionId: PropTypes.string,
};

const dispatchToProps = {
  loadPageData: fetchFixturesPageData,
};

const mapStateToProps = (state, { teamId, seasonId, competitionId }) => ({
  loading: isLoading(state, teamId, seasonId),
  previouslyLoaded: hasLoaded(state, teamId, seasonId),
  loadingError: getLoadingError(state, teamId),
  seasons: getCurrentClubSeasons(state),
  competitions: getCompetitions(state, teamId, seasonId),
  fixtures: getFixtures(state, teamId, seasonId, competitionId),
});

export default connect(mapStateToProps, dispatchToProps)(withTeam(TeamFixturesPage));
